import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { sportTranslationKey } from 'helpers/sportI18n';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { MobileMenuItemProps } from 'components/mobile/MobileMenuItem/MobileMenuItem';
import { VipIcon, VipIconName } from '../VipChrome/VipIcons';
import {
  BasketballArt,
  FootballArt,
  HockeyArt,
  SportBall,
  TennisArt,
  VipArtDefs,
} from '../VipChrome/VipSportArt';
import { SportIDs } from 'types/enums';
import type { Sport } from 'types/Sport';
import styles from './VipSports.module.css';

const SHORTCUT_ICONS: Record<string, VipIconName> = {
  'my-favorites': 'star',
  'live-betting': 'live',
  highlights: 'flame',
  'most-played-events': 'flame',
  'last-minute-events': 'bolt',
  'today-events': 'calendar',
};

/** The four biggest sports get a card with a picture, in this order. */
const BIG_SPORTS: [number, () => JSX.Element][] = [
  [SportIDs.SOCCER, FootballArt],
  [SportIDs.TENNIS, TennisArt],
  [SportIDs.ICE_HOCKEY, HockeyArt],
  [SportIDs.BASKETBALL, BasketballArt],
];

interface Props {
  sports: Sport[];
  time?: string | null;
  resultsMode?: boolean;
  shortcuts: MobileMenuItemProps[];
}

/** VIP sports page: the small tools of the home in a sideways row, the big
 *  sports as cards with their picture, the others as small tiles. */
export const VipSports = ({ sports, time, resultsMode, shortcuts }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const list = sports ?? [];
  const name = (s: Sport) =>
    t(sportTranslationKey(s.id), { defaultValue: s.name });
  const open = (s: Sport) =>
    navigate(
      `${resultsMode ? '/results' : '/sports-betting'}/sport/${s.name}/${s.id}${
        time ? `?time=${time}` : ''
      }`
    );
  const big = BIG_SPORTS.filter(([id]) => list.some((s) => s.id === id));
  const small = list.filter((s) => !BIG_SPORTS.some(([id]) => id === s.id));

  const card = (s: Sport, art: ReactNode) => (
    <button
      type="button"
      key={s.id}
      className={classNames(styles.card, styles.big)}
      data-vip-dark
      onClick={() => open(s)}
    >
      <span className={styles.glow} />
      <span className={styles.stage}>{art}</span>
      <span className={styles.label}>
        {name(s)}
        {!!s.count && <small>{t('vip_matches', { count: s.count })}</small>}
      </span>
    </button>
  );

  return (
    <div className={styles.page}>
      <VipArtDefs />
      {shortcuts.length > 0 && (
        <div className={styles.tools}>
          {shortcuts.map((x) => (
            <button
              type="button"
              key={x.iconKey}
              className={styles.tool}
              onClick={() => x.destinationUrl && navigate(x.destinationUrl)}
            >
              <span className={styles.toolIcon}>
                <VipIcon name={SHORTCUT_ICONS[x.iconKey] ?? 'star'} size={16} />
              </span>
              {x.label}
            </button>
          ))}
        </div>
      )}
      {list.length === 0 && (
        <p className={styles.empty}>{t('no_events_in_period')}</p>
      )}
      {big.length > 0 && (
        <div className={styles.bigGrid}>
          {big.map(([id, Art]) =>
            card(list.find((s) => s.id === id)!, <Art />)
          )}
        </div>
      )}
      {small.length > 0 && (
        <div className={styles.smallGrid}>
          {small.map((s) => (
            <button
              type="button"
              key={s.id}
              className={classNames(styles.card, styles.tile)}
              onClick={() => open(s)}
            >
              <span className={styles.mini}>
                {SportBall({ sportId: s.id }) ?? (
                  <SportIcon
                    sportId={s.id}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = getDefaultSportIconSrc();
                    }}
                    alt=""
                  />
                )}
              </span>
              <span className={styles.tileText}>
                <span>{name(s)}</span>
                {!!s.count && <small>{s.count}</small>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
